import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

// Ticket 151: shared "clamp + expand" for long text in table cells.
// Collapsed: clamped to `lines` lines with an ellipsis, capped at `maxWidth`.
// A "Show more" toggle appears only when the text really overflows (measured
// from the rendered box, not string length). Expanded: full text, and long
// URLs / emails / slugs may break anywhere so they can't force a scroll.
//
// `minWidth` is a floor on the cell box. Without it, an auto-layout table wider
// than its scroller shrinks breakable text columns to their longest word.
// Default: min(maxWidth, 10rem). Numbers are px; strings are any CSS length.
//
// `text` is the plain string (used for the title tooltip and as the default
// content); pass `children` to render richer content (a link) in the same box.
export default function LongTextCell({
  id,
  text,
  children,
  lines = 2,
  maxWidth = 240,
  minWidth,
  className,
}) {
  const autoId = useId();
  const key = id ?? autoId;
  const bodyId = `${autoId}-body`;
  const bodyRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);

  const measure = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    setOverflowing(el.scrollHeight > el.clientHeight + 1 || el.scrollWidth > el.clientWidth + 1);
  }, []);

  // Only measure while collapsed: expanded has nothing hidden to detect.
  useLayoutEffect(() => {
    if (!expanded) measure();
  }, [expanded, text, lines, maxWidth, measure]);

  useEffect(() => {
    if (expanded || typeof ResizeObserver === 'undefined' || !bodyRef.current) return undefined;
    const ro = new ResizeObserver(() => measure());
    ro.observe(bodyRef.current);
    return () => ro.disconnect();
  }, [expanded, measure]);

  const maxCss = typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth;
  const minCss = minWidth === undefined || minWidth === null
    ? `min(${maxCss}, 10rem)`
    : (typeof minWidth === 'number' ? `${minWidth}px` : minWidth);

  const content = children ?? text;
  if (content === null || content === undefined || content === '') return null;

  const clampStyle = expanded
    ? undefined
    : {
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: lines,
        overflow: 'hidden',
      };

  return (
    <div className="min-w-0" style={{ maxWidth, minWidth: minCss }}>
      <div
        ref={bodyRef}
        id={bodyId}
        title={typeof text === 'string' ? text : undefined}
        data-testid={`long-text-${key}`}
        // whitespace-normal resets nowrap inherited from a <th> / TableScroller.
        className={cn('whitespace-normal', expanded ? '[overflow-wrap:anywhere]' : '[overflow-wrap:break-word]', className)}
        style={clampStyle}
      >
        {content}
      </div>
      {(overflowing || expanded) && (
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={bodyId}
          data-testid={`long-text-toggle-${key}`}
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
          className="mt-0.5 text-[11px] font-semibold text-slate-500 hover:text-slate-800 hover:underline"
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
}
